import {CmLinkableType} from "../../../types";
import { CmLinkableWithChildren } from "./types/navigation";

/**
 * Type definition for navigation match result
 */
type NavigationMatch = {
    category: CmLinkableType;
    subcategory?: CmLinkableType;
    level: number;
} | null;

/**
 * Checks if a URL matches a navigation item
 * @param item Navigation item to check
 * @param url URL to match against
 * @returns True if the URL matches the item
 */
const isUrlMatch = (item: CmLinkableType, url: string): boolean => {
    return !!item.selfUrl && url.startsWith(item.selfUrl);
};

/**
 * Checks if a navigation item has children
 * @param item Navigation item to check
 * @returns True if the item has children
 */
const hasChildren = (item: CmLinkableType): boolean => {
    const itemWithChildren = item as CmLinkableWithChildren;
    return !!itemWithChildren.children && itemWithChildren.children.length > 0;
};

/**
 * Finds a matching item in the third level (sub-subcategories)
 * @param subcategory Parent subcategory
 * @param url URL to match against
 * @returns True if a match is found
 */
const findThirdLevelMatch = (subcategory: CmLinkableType, url: string): boolean => {
    if (!hasChildren(subcategory)) return false;

    const subcategoryWithChildren = subcategory as CmLinkableWithChildren;
    return subcategoryWithChildren.children!.some(subSubcategory => 
        isUrlMatch(subSubcategory, url)
    );
};

/**
 * Finds a matching subcategory in the second level
 * @param category Parent category
 * @param url URL to match against
 * @returns Matching subcategory or null
 */
const findSecondLevelMatch = (category: CmLinkableType, url: string): CmLinkableType | null => {
    if (!hasChildren(category)) return null;

    const categoryWithChildren = category as CmLinkableWithChildren;
    for (const subcategory of categoryWithChildren.children!) {
        if (isUrlMatch(subcategory, url)) {
            return subcategory;
        }
    }

    return null;
};

/**
 * Finds the category and subcategory for the current page
 * @param items Array of menu items
 * @param currentPageUrl URL of the current page
 * @returns Object containing the matching category, subcategory, and level
 */
export const findNavigationMatch = (items: Array<CmLinkableType>, currentPageUrl: string): NavigationMatch => {
    if (!currentPageUrl) return null;

    // Iterate through first level categories
    for (const category of items) {
        if (!isUrlMatch(category, currentPageUrl)) continue;

        // Try to find a match in the second level
        const subcategory = findSecondLevelMatch(category, currentPageUrl);
        if (subcategory) {
            // Check if there's a match in the third level
            if (findThirdLevelMatch(subcategory, currentPageUrl)) {
                return { category, subcategory, level: 2 };
            }

            // Found a match at the second level
            return { category, subcategory, level: 1 };
        }

        // Found a match at the first level
        return { category, level: 0 };
    }

    return null;
};
